import { memo, useCallback, useEffect, useRef } from 'react'

import { MediaLibraryEntryEdit } from '@nativewaves/nw-cloud-sdk/media'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { useMediaLibraryMediaUpdateMutation } from 'hooks/mutations/publishing'
import { mediaLibraryMediaIndexRoute } from 'pages/NWPlatform/MediaLibraries/MediaLibrary/Media'
import { mediaLibraryQueryKeys } from 'services/queryKeys'

const JSONRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const { mediaLibraryId, mediaId } = mediaLibraryMediaIndexRoute.useParams()

  const mediaLibraryMediaQuery = useQuery(mediaLibraryQueryKeys.mediaDetail({ libraryId: mediaLibraryId, mediaId }))
  const { data: mediaLibraryMedia } = mediaLibraryMediaQuery

  const { mutate: updateMedia } = useMediaLibraryMediaUpdateMutation()

  const handleFormSubmit = useCallback(
    (data: MediaLibraryEntryEdit) => {
      updateMedia(
        { libraryId: mediaLibraryId, mediaId, mediaLibraryEntryEdit: data },
        {
          onSuccess: () => {
            enqueueSnackbar(t('domain:MediaLibrary.mediaSaved'), {
              variant: 'success',
            })
          },
        },
      )
    },
    [enqueueSnackbar, mediaId, mediaLibraryId, t, updateMedia],
  )

  useEffect(() => {
    if (editorInstanceRef.current && mediaLibraryMediaQuery.isSuccess) {
      editorInstanceRef.current?.getModel()?.setValue(window.JSON.stringify(mediaLibraryMedia, null, 4))
    }
  }, [mediaLibraryMediaQuery.isSuccess, mediaLibraryMedia])

  if (mediaLibraryMediaQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!mediaLibraryMedia) {
    return <NotFound />
  }

  return (
    <Editor
      value={mediaLibraryMedia}
      onSubmit={handleFormSubmit}
      getEditorInstance={(editorInstance: EditorInstance) => (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const JSON = memo(JSONRaw)
